/**
 * File Store - Handles Blob storage and object URL management
 * Stores files in IndexedDB and provides helpers for URL creation/revocation
 * 
 * ENHANCEMENT 1: Proper Object URL lifecycle management
 */
import db, { generateId, timestamp } from './localDb';

// Track active object URLs to prevent memory leaks
// Map<fileId|trackingKey, { url: string, refCount: number }>
const activeUrls = new Map();

/**
 * Save a file (Blob or base64) to IndexedDB
 * @param {Blob|string} data - Blob or base64 string
 * @param {string} entityType - Type of entity (firearm, ammunition, etc.)
 * @param {string} entityId - ID of the parent entity
 * @param {string} mimeType - MIME type of the file
 * @returns {Promise<string>} - File ID
 */
export async function saveFile(data, entityType, entityId, mimeType) {
  let blob;
  
  if (data instanceof Blob) {
    blob = data;
  } else if (typeof data === 'string') {
    // Assume base64
    blob = base64ToBlob(data, mimeType);
  } else {
    throw new Error('Invalid file data: must be Blob or base64 string');
  }
  
  const fileId = generateId('file');
  await db.files.add({
    id: fileId,
    entityType,
    entityId,
    blob,
    mimeType,
    created_at: timestamp()
  });
  
  return fileId;
}

/**
 * Get a file by ID
 * @param {string} fileId - File ID
 * @returns {Promise<{blob: Blob, mimeType: string}|null>}
 */
export async function getFile(fileId) {
  const file = await db.files.get(fileId);
  if (!file) return null;
  return { blob: file.blob, mimeType: file.mimeType };
}

/**
 * Get all files from the database
 * @returns {Promise<Array>}
 */
export async function getAllFiles() {
  return await db.files.toArray();
}

/**
 * Delete a file by ID
 * @param {string} fileId - File ID
 */
export async function deleteFile(fileId) {
  // Revoke any active URL for this file
  if (activeUrls.has(fileId)) {
    const entry = activeUrls.get(fileId);
    URL.revokeObjectURL(entry.url);
    activeUrls.delete(fileId);
  }
  await db.files.delete(fileId);
}

/**
 * Delete all files for an entity
 * @param {string} entityType - Type of entity
 * @param {string} entityId - Entity ID
 */
export async function deleteFilesForEntity(entityType, entityId) {
  const files = await db.files.where({ entityType, entityId }).toArray();
  for (const file of files) {
    if (activeUrls.has(file.id)) {
      const entry = activeUrls.get(file.id);
      URL.revokeObjectURL(entry.url);
      activeUrls.delete(file.id);
    }
  }
  await db.files.where({ entityType, entityId }).delete();
}

/**
 * ENHANCEMENT 1: Get object URL for a file with proper lifecycle management
 * Returns an object with the URL and a revoke function for cleanup
 * @param {string} fileId - File ID
 * @returns {Promise<{url: string, revoke: () => void}|null>}
 */
export async function getObjectUrlForFileId(fileId) {
  if (!fileId) return null;
  
  // Check if we already have an active URL
  if (activeUrls.has(fileId)) {
    const entry = activeUrls.get(fileId);
    entry.refCount++;
    return {
      url: entry.url,
      revoke: () => releaseObjectUrl(fileId)
    };
  }
  
  // Load file and create new URL
  const file = await getFile(fileId);
  if (!file) return null;
  
  const url = URL.createObjectURL(file.blob);
  activeUrls.set(fileId, { url, refCount: 1 });
  
  return {
    url,
    revoke: () => releaseObjectUrl(fileId)
  };
}

/**
 * ENHANCEMENT 1: Release/decrement reference to an object URL
 * Actually revokes when refCount reaches 0
 * @param {string} fileId - File ID
 */
export function releaseObjectUrl(fileId) {
  if (!activeUrls.has(fileId)) return;
  
  const entry = activeUrls.get(fileId);
  entry.refCount--;
  
  if (entry.refCount <= 0) {
    URL.revokeObjectURL(entry.url);
    activeUrls.delete(fileId);
  }
}

/**
 * Create an object URL for a file (legacy - use getObjectUrlForFileId for new code)
 * Tracks the URL for cleanup
 * @param {string} fileId - File ID
 * @returns {Promise<string|null>} - Object URL or null if file not found
 */
export async function createObjectUrl(fileId) {
  const result = await getObjectUrlForFileId(fileId);
  return result ? result.url : null;
}

/**
 * Create an object URL from a Blob directly
 * @param {Blob} blob - The blob to create URL for
 * @param {string} [trackingKey] - Optional key to track this URL
 * @returns {string} - Object URL
 */
export function createObjectUrlFromBlob(blob, trackingKey) {
  const url = URL.createObjectURL(blob);
  if (trackingKey) {
    activeUrls.set(trackingKey, { url, refCount: 1 });
  }
  return url;
}

/**
 * Revoke an object URL
 * @param {string} fileIdOrUrl - File ID or the URL itself
 */
export function revokeObjectUrl(fileIdOrUrl) {
  if (activeUrls.has(fileIdOrUrl)) {
    const entry = activeUrls.get(fileIdOrUrl);
    URL.revokeObjectURL(entry.url);
    activeUrls.delete(fileIdOrUrl);
  } else if (fileIdOrUrl && fileIdOrUrl.startsWith('blob:')) {
    URL.revokeObjectURL(fileIdOrUrl);
    // Find and remove from tracking
    for (const [key, entry] of activeUrls.entries()) {
      if (entry.url === fileIdOrUrl) {
        activeUrls.delete(key);
        break;
      }
    }
  }
}

/**
 * Revoke all tracked object URLs
 * Call this when unmounting components or cleaning up
 */
export function revokeAllObjectUrls() {
  for (const entry of activeUrls.values()) {
    URL.revokeObjectURL(entry.url);
  }
  activeUrls.clear();
}

/**
 * Get count of active object URLs (for debugging)
 * @returns {number}
 */
export function getActiveUrlCount() {
  return activeUrls.size;
}

/**
 * Get file as base64 (for compatibility with existing code)
 * @param {string} fileId - File ID
 * @returns {Promise<string|null>} - Base64 data URL or null
 */
export async function getFileAsBase64(fileId) {
  const file = await getFile(fileId);
  if (!file) return null;
  return blobToBase64(file.blob);
}

/**
 * Convert base64 to Blob
 * @param {string} base64 - Base64 string (with or without data URL prefix)
 * @param {string} mimeType - MIME type fallback
 * @returns {Blob}
 */
export function base64ToBlob(base64, mimeType = 'application/octet-stream') {
  let base64Data = base64;
  let detectedMimeType = mimeType;
  
  // Handle data URL format: data:image/png;base64,xxxxx
  if (base64.startsWith('data:')) {
    const parts = base64.split(',');
    const mimeMatch = parts[0].match(/:(.*?);/);
    if (mimeMatch) detectedMimeType = mimeMatch[1];
    base64Data = parts[1];
  }
  
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: detectedMimeType });
}

/**
 * Convert Blob to base64
 * @param {Blob} blob - Blob to convert
 * @returns {Promise<string>} - Base64 data URL
 */
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Convert Blob to ArrayBuffer
 * @param {Blob} blob
 * @returns {Promise<ArrayBuffer>}
 */
export function blobToArrayBuffer(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Process images array - converts base64 images to blob storage
 * Returns an array of file IDs
 * IMPORTANT: Preserves existing file IDs (starting with 'file_') without re-saving
 * @param {string[]} images - Array of base64 image strings or existing file IDs
 * @param {string} entityType - Type of entity
 * @param {string} entityId - Entity ID
 * @returns {Promise<string[]>} - Array of file IDs
 */
export async function processImages(images, entityType, entityId) {
  if (!images || !Array.isArray(images)) return [];
  
  const fileIds = [];
  for (const img of images) {
    if (typeof img === 'string') {
      // If it's already a file ID, preserve it without re-saving
      if (img.startsWith('file_')) {
        fileIds.push(img);
        continue;
      }
      
      // If it's a base64 data URL, save as new file
      if (img.startsWith('data:')) {
        const match = img.match(/data:(.*?);/);
        const mimeType = match ? match[1] : 'image/jpeg';
        const fileId = await saveFile(img, entityType, entityId, mimeType);
        fileIds.push(fileId);
      }
      // Skip other string formats (blob URLs, http URLs, etc.) - they shouldn't be saved
    }
  }
  return fileIds;
}

/**
 * Load images for an entity - converts file IDs to object URLs
 * @param {string[]} fileIds - Array of file IDs
 * @returns {Promise<string[]>} - Array of object URLs
 */
export async function loadImages(fileIds) {
  if (!fileIds || !Array.isArray(fileIds)) return [];
  
  const urls = [];
  for (const fileId of fileIds) {
    const url = await createObjectUrl(fileId);
    if (url) urls.push(url);
  }
  return urls;
}

/**
 * Load images as base64 (for compatibility)
 * @param {string[]} fileIds - Array of file IDs
 * @returns {Promise<string[]>} - Array of base64 data URLs
 */
export async function loadImagesAsBase64(fileIds) {
  if (!fileIds || !Array.isArray(fileIds)) return [];
  
  const base64s = [];
  for (const fileId of fileIds) {
    const base64 = await getFileAsBase64(fileId);
    if (base64) base64s.push(base64);
  }
  return base64s;
}

/**
 * Clear all files from the database (used during import)
 */
export async function clearAllFiles() {
  // Revoke all active URLs first
  revokeAllObjectUrls();
  await db.files.clear();
}

/**
 * Bulk add files (used during import)
 * @param {Array} files - Array of file objects with id, entityType, entityId, blob, mimeType
 */
export async function bulkAddFiles(files) {
  await db.files.bulkAdd(files);
}

export default {
  saveFile,
  getFile,
  getAllFiles,
  deleteFile,
  deleteFilesForEntity,
  getObjectUrlForFileId,
  releaseObjectUrl,
  createObjectUrl,
  createObjectUrlFromBlob,
  revokeObjectUrl,
  revokeAllObjectUrls,
  getActiveUrlCount,
  getFileAsBase64,
  base64ToBlob,
  blobToBase64,
  blobToArrayBuffer,
  processImages,
  loadImages,
  loadImagesAsBase64,
  clearAllFiles,
  bulkAddFiles
};
